import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MapContainer, TileLayer, CircleMarker, Marker, Tooltip, ZoomControl, useMapEvents,
} from 'react-leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  classifyTrainType, trainTypeIcons, trainTypeColors, brandColorFor, readableTextOn,
} from './trainUtils';
import {
  trainGroupLabels, DELAY_THRESHOLD_MIN, delayColor, positionAt, segmentKey,
} from './trainPosition';
import {
  fetchActiveTrains, fetchKbsStatus, fetchKbsDetail,
  fetchKbsGeometry, fetchTrainJourney, fetchFilterOptions, fetchRouteSegments,
  fetchStations, fetchStationStats, fetchStationBoard, searchTrains, fetchNetworkStats,
  toOperatingDay, shiftOperatingDay, USE_MOCK,
} from './dashboardApi';
import { FEATURES } from '../config/features';
import MapControls, { countActiveFilters } from './MapControls';
import MapSearch from './MapSearch';
import KbsLayer from './KbsLayer';
import KbsDrillIn from './KbsDrillIn';
import StationPanel from './StationPanel';
import SearchResultsPanel from './SearchResultsPanel';
import NetworkDashboard from './NetworkDashboard';
import BottomSheet from './BottomSheet';
import TrainPanel from './TrainPanel';
import TrainRouteLayer from './TrainRouteLayer';
import '../css/liveMap.css';

const DEFAULT_CENTER = [51.0, 10.2];
const DEFAULT_ZOOM = 6;
// The dashboard concept settles on 60–90 s as realistic and bahn.de-friendly
const LIVE_POLL_MS = 60000;
const CLUSTER_MAX_ZOOM = 7;
const VIEW_DEBOUNCE_MS = 300;
const VIEW_MAX_WAIT_MS = 1000;
// "Zum Zug" zooms in at least this far, so the train is a dot with a label, not a cluster
const LOCATE_MIN_ZOOM = 11;
// How long a followed train may be missing from the snapshots before following ends
const FOLLOW_GRACE_MS = 3000;
const LABEL_MIN_ZOOM = 9;
const MAX_LABELS = 120;
const STATION_MIN_ZOOM = 7;
// The positions are fetched for more than the viewport: half a viewport extra on every side,
// snapped to a coarse grid. Panning within that area needs no request at all, and trains at
// the edge are already there when they scroll into view instead of popping in a poll later.
const FETCH_BBOX_PAD = 0.5;
const FETCH_BBOX_GRID_DEG = 0.5;
// Zoomed far into an area fetched from far out, the snapshot carries trains nobody can see.
// Past this ratio of fetched to visible area the next move asks for a tighter one.
const FETCH_BBOX_MAX_AREA_RATIO = 16;
// Trains drawn around the viewport, so a short pan does not reveal an empty margin
const DRAW_BBOX_PAD = 0.25;

const DELAY_LEGEND = [
  { color: '#3fb950', label: '< 3 min' },
  { color: '#f0b429', label: '3–5 min' },
  { color: '#f78166', label: '6–14 min' },
  { color: '#f85149', label: '≥ 15 min' },
  { color: '#8b949e', label: 'Ausfall' },
];

// ---------------------------------------------------------------------------
// Map view tracking (debounced so panning does not fire a request per frame)
// ---------------------------------------------------------------------------
const ViewTracker = ({ onViewChange }) => {
  const timer = useRef(null);
  const lastEmit = useRef(0);
  const emit = useCallback((map) => {
    const report = () => {
      lastEmit.current = Date.now();
      const b = map.getBounds();
      onViewChange({
        bbox: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
        zoom: map.getZoom(),
      });
    };
    clearTimeout(timer.current);
    // A map that never stops moving — following a train recentres it every frame — would
    // otherwise never report at all, and the train would run out of the drawn area
    if (Date.now() - lastEmit.current > VIEW_MAX_WAIT_MS) report();
    else timer.current = setTimeout(report, VIEW_DEBOUNCE_MS);
  }, [onViewChange]);

  const map = useMapEvents({
    moveend: () => emit(map),
    zoomend: () => emit(map),
  });

  useEffect(() => {
    const b = map.getBounds();
    onViewChange({
      bbox: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
      zoom: map.getZoom(),
    });
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
};

// ---------------------------------------------------------------------------
// Bounding boxes, as [west, south, east, north]
// ---------------------------------------------------------------------------
const bboxArea = ([w, s, e, n]) => Math.max(0, e - w) * Math.max(0, n - s);

const bboxContains = (outer, inner) =>
  outer[0] <= inner[0] && outer[1] <= inner[1] && outer[2] >= inner[2] && outer[3] >= inner[3];

const padBbox = ([w, s, e, n], pad) => {
  const dx = (e - w) * pad;
  const dy = (n - s) * pad;
  return [w - dx, s - dy, e + dx, n + dy];
};

/** The area to fetch for a view: padded, snapped outwards to the grid, clamped to the globe */
const fetchBboxFor = (view) => {
  const g = FETCH_BBOX_GRID_DEG;
  const [w, s, e, n] = padBbox(view, FETCH_BBOX_PAD);
  return [
    Math.max(-180, Math.floor(w / g) * g),
    Math.max(-90, Math.floor(s / g) * g),
    Math.min(180, Math.ceil(e / g) * g),
    Math.min(90, Math.ceil(n / g) * g),
  ];
};

const inBbox = (bbox, t) =>
  t.lng >= bbox[0] && t.lat >= bbox[1] && t.lng <= bbox[2] && t.lat <= bbox[3];

// ---------------------------------------------------------------------------
// Movement: the snapshot carries the previous and the next stop with their times, so the
// position for *any* instant can be computed locally. That is what keeps the trains moving
// continuously between two polls — blending between snapshots instead would always trail a
// full poll interval behind reality.
// ---------------------------------------------------------------------------
// positionAt, walkSegment and segmentKey live in trainPosition.js with the rest of the
// position maths, so they can be tested without mounting a map.

// ---------------------------------------------------------------------------
// Flightradar-style labels: product symbol, line/train name and operator next to the dot.
// The DB product SVGs are rendered to markup once per type, never per train.
// ---------------------------------------------------------------------------
const ICON_MARKUP = Object.fromEntries(
  Object.entries(trainTypeIcons).map(([type, node]) => [type, renderToStaticMarkup(node)])
);

// Short operator marks. A logo at /operators/<slug>.png wins when it exists — the text is
// what the label falls back to, so missing assets simply look plainer.
const OPERATOR_SHORT = [
  [/^DB (Fernverkehr|Regio)/i, 'DB'],
  [/^DB /i, 'DB'],
  [/metronom/i, 'ME'],
  [/national express/i, 'NX'],
  [/odeg/i, 'ODEG'],
  [/^S-Bahn (Hamburg|Berlin)/i, 'S'],
  [/hochbahn/i, 'HHA'],
  [/^BVG/i, 'BVG'],
  [/^MVG/i, 'MVG'],
  [/^KVB/i, 'KVB'],
];

const operatorShort = (operator) => {
  if (!operator) return null;
  const hit = OPERATOR_SHORT.find(([re]) => re.test(operator));
  if (hit) return hit[1];
  return operator.split(/\s+/).map(w => w[0]).join('').slice(0, 4).toUpperCase();
};

const operatorSlug = (operator) =>
  (operator || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Operator logos are optional assets. A missing file must never reach the label as a broken
// <img> — that renders as an empty box next to the symbol. So each slug is probed once and
// the logo is only put into the markup after it actually loaded; until then the text mark
// stands. The label ticker repaints anyway, so no subscription is needed.
const logoStatus = new Map();

const probeLogo = (slug) => {
  if (!slug) return 'missing';
  const known = logoStatus.get(slug);
  if (known) return known;
  logoStatus.set(slug, 'pending');
  const img = new Image();
  img.onload = () => logoStatus.set(slug, 'ok');
  img.onerror = () => logoStatus.set(slug, 'missing');
  img.src = `/operators/${slug}.png`;
  return 'pending';
};

// Icons are cached because Leaflet replaces the DOM node whenever the icon object changes —
// recreating them every animation frame would make the labels flicker.
const labelIconCache = new Map();

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, ch => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
));

// `operator` is who runs this leg, out of the journey's HAFAS administration code; it falls
// back to the class-based name where the code names a subsidiary we cannot resolve. The class
// alone had an EC Zürich–Praha labelled "DB Fernverkehr" and left 22 of 43 trains with a Swiss
// stop with no operator at all.
const operatorOf = (train) => train.operator || train.trainSerieOperator;

const brandColor = (train) => brandColorFor(operatorOf(train));

const trainLabelIcon = (train, selected = false) => {
  const type = classifyTrainType(train);
  const short = operatorShort(operatorOf(train));
  const slug = operatorSlug(operatorOf(train));
  const logo = probeLogo(slug);
  const name = (train.trainName || '').trim();
  const line = (train.trainLineName || '').trim();
  // The line is what passengers know the train by (S8, RE 1), the name identifies the run
  // (ICE 1243). Show both, unless they say the same thing.
  const showLine = line && line.toLowerCase() !== name.toLowerCase();
  const label = escapeHtml(name);
  const delayed = train.current_delay >= DELAY_THRESHOLD_MIN;
  const key = `${type}|${line}|${label}|${short}|${logo}|${delayed}|${train.status}|${selected}`;

  let icon = labelIconCache.get(key);
  if (icon) return icon;

  const brand = brandColor(train);
  const color = brand || trainTypeColors[type] || trainTypeColors.default;
  const lineChip = showLine
    ? `<span class="mtl-line" style="background:${color};color:${readableTextOn(color)}">`
      + `${escapeHtml(line)}</span>`
    : '';

  const delayChip = train.status === 'cancelled'
    ? '<span class="mtl-delay cancelled">Ausfall</span>'
    : train.current_delay > 0
      ? `<span class="mtl-delay${delayed ? ' late' : ''}">+${train.current_delay}</span>`
      : '';
  // An ICE carries no line name, so without this the brand would have nowhere to show. The
  // operator mark is the honest place for it anyway: it is the operator's own badge.
  const brandStyle = brand
    ? ` style="background:${brand};color:${readableTextOn(brand)};border-color:${brand}"`
    : '';
  const operatorChip = short
    ? (logo === 'ok'
      ? `<span class="mtl-op"><img alt="${escapeHtml(short)}" src="/operators/${slug}.png"></span>`
      : `<span class="mtl-op"${brandStyle}>${escapeHtml(short)}</span>`)
    : '';

  icon = L.divIcon({
    className: 'map-train-label-icon',
    html: `<div class="map-train-label${selected ? ' selected' : ''}" title="Details öffnen">`
      + `<span class="mtl-icon">${ICON_MARKUP[type] || ICON_MARKUP.default}</span>`
      + `${lineChip}<span class="mtl-name">${label}</span>${operatorChip}${delayChip}</div>`,
    // No iconSize on purpose — Leaflet would write width/height straight onto the element and
    // collapse the label. Without it only iconAnchor is applied, as a negative margin.
    iconAnchor: [-9, 9],
  });
  labelIconCache.set(key, icon);
  return icon;
};

// The map repaints several times a second, and react-leaflet takes every new pathOptions
// object as a new style and every new icon as a new DOM node. Both are therefore handed out
// from caches, keyed on what they actually show.
const dotStyleCache = new Map();
const trainDotStyle = (train) => {
  const cancelled = train.status === 'cancelled';
  const fillColor = delayColor(train.current_delay, train.status);
  const key = `${fillColor}|${cancelled}`;
  let style = dotStyleCache.get(key);
  if (!style) {
    style = { color: '#ffffff', weight: 1, fillColor, fillOpacity: cancelled ? 0.5 : 0.95 };
    dotStyleCache.set(key, style);
  }
  return style;
};

const clusterIconCache = new Map();
const clusterIcon = (count, bad) => {
  const key = `${count}|${bad}`;
  let icon = clusterIconCache.get(key);
  if (!icon) {
    icon = L.divIcon({
      className: 'map-cluster-icon',
      html: `<div class="map-cluster ${bad ? 'bad' : ''}">${count}</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17],
    });
    clusterIconCache.set(key, icon);
  }
  return icon;
};

const STATION_ICONS = {};
const stationIcon = (selected) => {
  const key = selected ? 'on' : 'off';
  if (!STATION_ICONS[key]) {
    STATION_ICONS[key] = L.divIcon({
      className: 'map-station-marker',
      html: `<span class="map-station-dot${selected ? ' selected' : ''}"></span>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
  }
  return STATION_ICONS[key];
};

/** Phone layout — the panels become a bottom sheet and the search collapses to a pill */
const useIsMobile = (breakpoint = 768) => {
  const query = `(max-width: ${breakpoint}px)`;
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return isMobile;
};

/** Repaint ticker — 8 fps is smooth enough for train speeds and cheap for a few hundred dots */
const useAnimationFrames = (active, fps = 8) => {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    let raf = null;
    let last = 0;
    const interval = 1000 / fps;
    const loop = () => {
      const now = Date.now();
      if (now - last >= interval) { last = now; setFrame(f => f + 1); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, fps]);
  return frame;
};

/**
 * Identity of a run. The backend hands out a UUID and a numeric globalTrainID; the UUID is
 * what every other endpoint is keyed on, so it comes first.
 */
const trainKey = (train) => train?.trainUUID || train?.globalTrainID || null;

/**
 * Build a panel-shaped train from a run alone, for trains that are not on the map at the
 * chosen moment — cancelled ones, or a run that does not exist on that operating day.
 * Everything that needs a live position is left out; the panel handles that.
 */
const journeyToTrain = (journey, trainUUID) => {
  const stations = journey.stations || [];
  const last = stations[stations.length - 1];
  return {
    trainUUID,
    globalTrainID: trainUUID,
    journeyUUID: journey.journeyUUID,
    trainName: journey.trainName,
    trainNumber: journey.trainNumber,
    trainLineName: journey.trainLineName,
    trainTypeProductCategorie: journey.trainTypeProductCategorie,
    trainTypeValue: journey.trainTypeValue,
    trainSerieNumber: journey.trainSeries?.[0]?.trainSerieNumber,
    trainSerieName: journey.trainSeries?.[0]?.trainSerieName,
    trainSerieOperator: journey.trainSeries?.[0]?.trainSerieOperator,
    terminus: last?.stationName,
    operatingDate: journey.operatingDate,
    status: journey.cancelled ? 'cancelled' : 'offmap',
    current_delay: 0,
    lat: null,
    lng: null,
    prev_station: null,
    next_station: null,
  };
};

// ---------------------------------------------------------------------------
// Grid clustering for low zoom levels — keeps the DOM small when zoomed out
// ---------------------------------------------------------------------------
const clusterTrains = (trains, zoom) => {
  const cell = zoom <= 5 ? 2.2 : 1.2;
  const cells = new Map();
  for (const t of trains) {
    const key = `${Math.floor(t.lat / cell)}|${Math.floor(t.lng / cell)}`;
    let c = cells.get(key);
    if (!c) {
      c = { latSum: 0, lngSum: 0, count: 0, delayed: 0 };
      cells.set(key, c);
    }
    c.latSum += t.lat;
    c.lngSum += t.lng;
    c.count += 1;
    if (t.current_delay >= DELAY_THRESHOLD_MIN) c.delayed += 1;
  }
  return Array.from(cells.entries()).map(([key, c]) => ({
    key,
    lat: c.latSum / c.count,
    lng: c.lngSum / c.count,
    count: c.count,
    delayedShare: c.delayed / c.count,
  }));
};

/** Side panel on the desktop, bottom sheet on the phone — same children either way */
const PanelHost = ({ open, mobile, onClose, children }) => {
  if (!open) return null;
  if (!mobile) return children;
  return <BottomSheet open onClose={onClose}>{children}</BottomSheet>;
};

// ---------------------------------------------------------------------------
const LiveMap = ({ onTrainSelect, focusRequest }) => {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState('live');
  const [opDay, setOpDay] = useState(() => toOperatingDay(new Date()));
  const [timeMin, setTimeMin] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(10);
  const [filters, setFilters] = useState({ types: [], line: null, serie: null, station: null, kbs: null });
  const [options, setOptions] = useState({ lines: [], series: [], stations: [], kbs: [] });

  const [trains, setTrains] = useState([]);
  const [kbsStatus, setKbsStatus] = useState([]);
  const [kbsGeometry, setKbsGeometry] = useState([]);
  const [loading, setLoading] = useState(false);

  const [view, setView] = useState({ bbox: null, zoom: DEFAULT_ZOOM });
  // The area the positions were asked for — larger than the view, see fetchBboxFor
  const [fetchBbox, setFetchBbox] = useState(null);
  // Everything the trains do not need waits for their first answer, see "static data"
  const [firstSnapshot, setFirstSnapshot] = useState(false);
  const [mapGrey, setMapGrey] = useState(false);
  const [showOrm, setShowOrm] = useState(false);
  const [showKbs, setShowKbs] = useState(true);
  const [controlsCollapsed, setControlsCollapsed] = useState(false);

  const [showStations, setShowStations] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [stations, setStations] = useState([]);

  // One panel slot for train, station, KBS and search — keeping four booleans in sync by
  // hand is how "two panels open at once" bugs get written.
  const [panel, setPanel] = useState(null);
  const selectedTrain = panel?.type === 'train' ? panel.train : null;
  const selectedKbs = panel?.type === 'kbs' ? panel.number : null;
  const selectedStation = panel?.type === 'station' ? panel.station : null;

  const [selectedJourney, setSelectedJourney] = useState(null);
  const [journeyState, setJourneyState] = useState('idle'); // idle | loading | ready | failed
  // Runs already read, by train. Survives a train dropping out of a snapshot and back in.
  const journeyCache = useRef(new Map());
  const [kbsDetail, setKbsDetail] = useState(null);
  const [kbsDetailLoading, setKbsDetailLoading] = useState(false);
  const [kbsDetailFailed, setKbsDetailFailed] = useState(false);
  const [kbsMetric, setKbsMetric] = useState('punctuality');
  const [pinnedKbs, setPinnedKbs] = useState(() => new Set());

  const [stationStats, setStationStats] = useState(null);
  const [stationLoading, setStationLoading] = useState(false);
  const [boardType, setBoardType] = useState('departure');
  const [board, setBoard] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);

  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [networkStats, setNetworkStats] = useState(null);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(true);

  // Track geometry per station pair. It belongs to the rails, not to a train, so it is
  // fetched once per pair and kept for the session — the snapshots stay small and a pair
  // already seen costs nothing on the next poll.
  const [segments, setSegments] = useState(() => new Map());
  const requestedSegments = useRef(new Set());

  // A train that a station board pointed at: marked on the map, but the panel keeps showing
  // the station until the user clicks the train itself
  const [highlightedTrain, setHighlightedTrain] = useState(null);
  const [boardNotice, setBoardNotice] = useState(null);

  const [controlsSheetOpen, setControlsSheetOpen] = useState(false);
  const activeFilters = countActiveFilters(filters);

  const [refreshTick, setRefreshTick] = useState(0);
  const pendingFocus = useRef(null);
  const mapRef = useRef(null);
  const dashboardRef = useRef(null);

  /** The map keeps the drag, so the way to the figures below it has to be a button */
  const scrollToDashboard = () => {
    setControlsSheetOpen(false);
    setDashboardOpen(true);
    dashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- static data ------------------------------------------------------
  // Held back until the first snapshot is in. These used to go out on mount, six requests
  // ahead of the positions — exactly the six connections a browser grants one host over
  // HTTP/1.1 — so the trains queued behind dropdown contents and route geometry.
  useEffect(() => {
    if (!firstSnapshot) return;
    fetchFilterOptions().then(setOptions).catch(() => {});
    fetchKbsGeometry().then(setKbsGeometry).catch(() => {});
  }, [firstSnapshot]);

  // Up to 5000 stations, for a layer that starts switched off: fetched when it is first shown.
  // That takes a few seconds, and a layer that is switched on but stays empty looks broken —
  // so the status is shown on the switch and on the map, without blocking either.
  const [stationsStatus, setStationsStatus] = useState('idle'); // idle | loading | ready | failed
  const [stationsAttempt, setStationsAttempt] = useState(0);
  useEffect(() => {
    // After a failure, switching the layer on again or the retry button asks once more
    if (!showStations || stationsStatus === 'loading' || stationsStatus === 'ready') return;
    setStationsStatus('loading');
    fetchStations()
      .then(list => { setStations(list); setStationsStatus('ready'); })
      .catch(() => setStationsStatus('failed'));
    // stationsStatus is what this effect sets — listing it would refire on its own update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStations, stationsAttempt]);
  const retryStations = () => setStationsAttempt(a => a + 1);

  // ---- area to fetch ----------------------------------------------------
  // Kept while the view stays inside it, so a pan or a zoom in costs no request
  useEffect(() => {
    if (!view.bbox) return;
    setFetchBbox(prev => (
      prev && bboxContains(prev, view.bbox)
        && bboxArea(prev) <= FETCH_BBOX_MAX_AREA_RATIO * bboxArea(view.bbox)
        ? prev
        : fetchBboxFor(view.bbox)
    ));
  }, [view.bbox]);

  // ---- clock for the point-in-time mode ---------------------------------
  useEffect(() => {
    if (mode !== 'point' || !playing) return undefined;
    const interval = setInterval(() => {
      setTimeMin(prev => (prev + speed) % 1440);
    }, 1000);
    return () => clearInterval(interval);
  }, [mode, playing, speed]);

  // ---- live polling -----------------------------------------------------
  useEffect(() => {
    if (mode !== 'live') return undefined;
    const interval = setInterval(() => setRefreshTick(t => t + 1), LIVE_POLL_MS);
    return () => clearInterval(interval);
  }, [mode]);

  const atDate = useMemo(() => {
    if (mode === 'live') return new Date();
    const [y, m, d] = opDay.split('-').map(Number);
    return new Date(y, m - 1, d, Math.floor(timeMin / 60), Math.round(timeMin) % 60, 0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, opDay, timeMin, refreshTick]);

  // ---- train positions --------------------------------------------------
  useEffect(() => {
    // No area yet means the map has not reported its view. Asking anyway sent one request
    // for the whole network and a second for the viewport straight after it.
    if (mode === 'kbs' || !fetchBbox) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchActiveTrains({ at: atDate, bbox: fetchBbox, filters })
      .then(res => { if (!cancelled) setTrains(res.trains); })
      .catch(() => { if (!cancelled) setTrains([]); })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setFirstSnapshot(true);
      });
    return () => { cancelled = true; };
  }, [mode, atDate, fetchBbox, filters]);

  // ---- KBS traffic light -------------------------------------------------
  useEffect(() => {
    if (mode !== 'kbs') return undefined;
    let cancelled = false;
    setLoading(true);
    setTrains([]);
    fetchKbsStatus({ opDay, filters })
      .then(res => { if (!cancelled) setKbsStatus(res); })
      .catch(() => { if (!cancelled) setKbsStatus([]); })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        // The traffic light is this mode's first answer — the route geometry may follow now
        setFirstSnapshot(true);
      });
    return () => { cancelled = true; };
  }, [mode, opDay, filters]);

  // ---- full run of the selected train ------------------------------------
  useEffect(() => {
    const key = trainKey(selectedTrain);
    if (!selectedTrain || !key) {
      setSelectedJourney(null); setJourneyState('idle');
      return undefined;
    }

    // A run once fetched is kept. Two reasons: a train that drops out of a snapshot and
    // comes back should not lose the route the user is looking at, and a failed request used
    // to leave the journey at null for good — the effect is keyed on the train, so selecting
    // the same train again changed nothing and never retried.
    const cached = journeyCache.current.get(key);
    if (cached) {
      setSelectedJourney(cached.journey);
      setJourneyState('ready');
    } else {
      setSelectedJourney(null);
      setJourneyState('loading');
    }

    let cancelled = false;
    fetchTrainJourney(selectedTrain)
      .then(journey => {
        if (cancelled) return;
        if (!journey) {
          // A missing run is an outcome, not a pending request — otherwise the panel spins
          // forever on trains the backend has no journey for. What is already shown stays.
          if (!cached) setJourneyState('failed');
          return;
        }
        journeyCache.current.set(key, { journey });
        setSelectedJourney(journey);
        setJourneyState('ready');
      })
      .catch(() => { if (!cancelled && !cached) setJourneyState('failed'); });
    return () => { cancelled = true; };
    // Keyed on the train, not the object identity — the snapshot is replaced every poll
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainKey(selectedTrain), selectedTrain?.operatingDate, refreshTick]);

  // ---- network-wide statistics for the shown operating day ----------------
  // Only computed while the dashboard is open — the monthly trend walks several days. It sits
  // below the map, so it waits for the trains like the rest of the static data.
  useEffect(() => {
    if (!dashboardOpen || !firstSnapshot) return undefined;
    let cancelled = false;
    setNetworkLoading(true);
    fetchNetworkStats({ opDay, filters })
      .then(res => { if (!cancelled) setNetworkStats(res); })
      .catch(() => { if (!cancelled) setNetworkStats(null); })
      .finally(() => { if (!cancelled) setNetworkLoading(false); });
    return () => { cancelled = true; };
  }, [dashboardOpen, firstSnapshot, opDay, filters]);

  // ---- KBS drill-in ------------------------------------------------------
  // Null from the fetch means "no data for this route and day", a throw means the request
  // failed. Both used to end as a null detail, and the drill-in rendered nothing at all — a
  // click on a route without data that day looked like a click that did nothing.
  useEffect(() => {
    if (!selectedKbs) { setKbsDetail(null); setKbsDetailFailed(false); return; }
    let cancelled = false;
    // The previous route's figures must not stand under the new route's name
    setKbsDetail(null);
    setKbsDetailFailed(false);
    setKbsDetailLoading(true);
    fetchKbsDetail({ kbsNumber: selectedKbs, opDay, filters })
      .then(res => { if (!cancelled) setKbsDetail(res); })
      .catch(() => { if (!cancelled) setKbsDetailFailed(true); })
      .finally(() => { if (!cancelled) setKbsDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedKbs, opDay, filters]);

  // Name and country of the selected route from the geometry, so the drill-in has a heading
  // even when the day has no figures for it
  const selectedKbsInfo = useMemo(
    () => (selectedKbs ? kbsGeometry.find(k => k.kbs_number === selectedKbs) || null : null),
    [kbsGeometry, selectedKbs]
  );

  // ---- track geometry for the sections the trains are on -----------------
  useEffect(() => {
    const missing = [];
    for (const t of trains) {
      const key = segmentKey(t);
      if (key && !requestedSegments.current.has(key)) {
        requestedSegments.current.add(key);
        missing.push(key);
      }
    }
    if (!missing.length) return undefined;

    let cancelled = false;
    // In batches: a full viewport can be a few hundred sections, and one request per section
    // would be worse than the straight line it replaces
    const batches = [];
    for (let i = 0; i < missing.length; i += 200) batches.push(missing.slice(i, i + 200));

    Promise.all(batches.map(batch =>
      fetchRouteSegments(batch).catch(() => {
        // Let a later poll try again rather than losing these sections for the session
        batch.forEach(key => requestedSegments.current.delete(key));
        return null;
      })
    )).then(results => {
      if (cancelled) return;
      const found = Object.assign({}, ...results.filter(Boolean));
      const usable = Object.entries(found)
        .filter(([, v]) => Array.isArray(v?.geometry) && v.geometry.length >= 2);
      if (!usable.length) return;
      setSegments(prev => {
        const next = new Map(prev);
        usable.forEach(([key, v]) => next.set(key, v.geometry));
        return next;
      });
    });

    return () => { cancelled = true; };
  }, [trains]);

  // ---- station statistics ------------------------------------------------
  useEffect(() => {
    if (!selectedStation) { setStationStats(null); return; }
    let cancelled = false;
    setStationLoading(true);
    fetchStationStats({
      stationUUID: selectedStation.uuid, stationName: selectedStation.name, opDay, filters,
    })
      .then(res => { if (!cancelled) setStationStats(res); })
      .catch(() => { if (!cancelled) setStationStats(null); })
      .finally(() => { if (!cancelled) setStationLoading(false); });
    return () => { cancelled = true; };
  }, [selectedStation, opDay, filters]);

  // ---- departure / arrival board, anchored on the map's moment -------------
  useEffect(() => {
    if (!selectedStation) { setBoard([]); return undefined; }
    let cancelled = false;
    setBoardLoading(true);
    fetchStationBoard({ stationUUID: selectedStation.uuid, at: atDate, type: boardType })
      .then(res => { if (!cancelled) setBoard(res); })
      .catch(() => { if (!cancelled) setBoard([]); })
      .finally(() => { if (!cancelled) setBoardLoading(false); });
    return () => { cancelled = true; };
  }, [selectedStation, boardType, atDate]);

  // ---- continuous clock --------------------------------------------------
  // In live mode this is the wall clock. In the point-in-time mode it is the simulated clock,
  // which keeps running between the one-second state updates so playback stays smooth at
  // every speed instead of stepping once per second.
  const clockAnchor = useRef({ sim: 0, real: 0, speed: 1, playing: false, live: true });
  useEffect(() => {
    clockAnchor.current = {
      sim: atDate.getTime(),
      real: Date.now(),
      speed,
      playing: mode === 'point' && playing,
      live: mode === 'live',
    };
  }, [atDate, speed, playing, mode]);

  const nowMs = useCallback(() => {
    const a = clockAnchor.current;
    if (a.live) return Date.now();
    if (!a.playing) return a.sim;
    // speed is simulated minutes per real second
    return a.sim + (Date.now() - a.real) * a.speed * 60;
  }, []);

  // ---- derived -----------------------------------------------------------
  // Below the cluster zoom nothing on screen moves visibly — the bubbles stand for a degree of
  // country each. The ticker only runs there while a single train is shown in focus.
  const frame = useAnimationFrames(
    mode !== 'kbs' && trains.length > 0
      && (view.zoom >= CLUSTER_MAX_ZOOM || !!selectedTrain || !!highlightedTrain)
  );

  const renderTrains = useMemo(() => {
    const now = nowMs();
    return trains.map(t => {
      const pos = positionAt(t, now, segments);
      if (!pos) return t;
      // The course from the geometry beats the backend's, which is the straight line between
      // the two stops and points wrong through every curve
      return {
        ...t, lat: pos.lat, lng: pos.lng,
        bearing: pos.bearing != null ? pos.bearing : t.bearing,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trains, frame, nowMs, segments]);

  const statusByNumber = useMemo(
    () => new Map(kbsStatus.map(k => [k.kbs_number, k])),
    [kbsStatus]
  );

  const visibleKbs = useMemo(() => {
    if (!filters.kbs) return kbsGeometry;
    return kbsGeometry.filter(k => k.kbs_number === filters.kbs);
  }, [kbsGeometry, filters.kbs]);

  // The snapshot covers more than the screen (fetchBboxFor). The figures describe what is in
  // view, the drawing takes a margin around it, and nothing further out is handed to Leaflet.
  const viewTrains = useMemo(
    () => (view.bbox ? renderTrains.filter(t => inBbox(view.bbox, t)) : renderTrains),
    [renderTrains, view.bbox]
  );
  const drawTrains = useMemo(() => {
    if (!view.bbox) return renderTrains;
    const area = padBbox(view.bbox, DRAW_BBOX_PAD);
    return renderTrains.filter(t => inBbox(area, t));
  }, [renderTrains, view.bbox]);

  const clusters = useMemo(
    () => (view.zoom < CLUSTER_MAX_ZOOM ? clusterTrains(drawTrains, view.zoom) : []),
    [drawTrains, view.zoom]
  );

  // Labels are real DOM nodes, so they are capped — beyond that the map turns into text soup
  // and the frame rate suffers. Delayed trains win the remaining slots. The selected train
  // always keeps its label, even with labels switched off or past the cap.
  const labelledTrains = useMemo(() => {
    const selectedId = selectedTrain?.globalTrainID;
    const withSelected = (list) => {
      if (!selectedId || list.some(t => t.globalTrainID === selectedId)) return list;
      const sel = renderTrains.find(t => t.globalTrainID === selectedId);
      return sel ? [...list, sel] : list;
    };
    if (!showLabels || view.zoom < LABEL_MIN_ZOOM) return withSelected([]);
    // Only trains on screen compete for the slots — one past the edge would take a label
    // nobody can see
    if (viewTrains.length <= MAX_LABELS) return withSelected(viewTrains);
    return withSelected(
      [...viewTrains]
        .sort((a, b) => (b.current_delay || 0) - (a.current_delay || 0))
        .slice(0, MAX_LABELS)
    );
  }, [renderTrains, viewTrains, view.zoom, showLabels, selectedTrain]);

  const stats = useMemo(() => {
    const active = viewTrains.filter(t => t.status !== 'cancelled');
    const onTime = active.filter(t => t.current_delay < DELAY_THRESHOLD_MIN).length;
    const byGroup = {};
    for (const group of ['fern', 'nah', 'sbahn']) {
      const rows = active.filter(t => t.train_group === group);
      byGroup[group] = rows.length > 0
        ? (rows.filter(t => t.current_delay < DELAY_THRESHOLD_MIN).length / rows.length) * 100
        : null;
    }
    const kbsTrains = kbsStatus.reduce((sum, k) => sum + k.train_count, 0);
    return {
      visible: viewTrains.length,
      cancelled: viewTrains.length - active.length,
      onTimePct: active.length > 0 ? (onTime / active.length) * 100 : null,
      avgDelay: active.length > 0
        ? active.reduce((sum, t) => sum + t.current_delay, 0) / active.length
        : 0,
      byGroup,
      kbsCount: kbsStatus.length,
      kbsTrains,
    };
  }, [viewTrains, kbsStatus]);

  // The live copy of the selected train, so the highlight keeps moving with it
  const liveSelectedTrain = useMemo(() => {
    if (!selectedTrain) return null;
    return renderTrains.find(t => t.globalTrainID === selectedTrain.globalTrainID) || selectedTrain;
  }, [renderTrains, selectedTrain]);

  /**
   * The train a board row pointed at. It is marked on the map like a selected one, but the
   * panel still shows the station — so the board stays readable and a click on the map is
   * what opens the train.
   */
  const markedTrain = useMemo(() => {
    if (!highlightedTrain) return null;
    return renderTrains.find(t => trainKey(t) === highlightedTrain) || null;
  }, [renderTrains, highlightedTrain]);

  const emphasisedTrain = liveSelectedTrain || markedTrain;

  // A train picked from a board or the search that is not running at this moment has no
  // position — the panel builds it from the run alone. Handing those nulls to Leaflet threw
  // inside its projection and took the whole page down, so the marker is drawn only for a
  // train that actually has coordinates.
  const hasPosition = (train) =>
    !!train && Number.isFinite(train.lat) && Number.isFinite(train.lng);

  // ---- "Zum Zug" and "Folgen" ---------------------------------------------
  // The selected train as it is on the map right now — null when this moment's snapshot does
  // not carry it, which is when neither button can do anything
  const selectedOnMap = useMemo(() => {
    if (!selectedTrain) return null;
    const hit = renderTrains.find(t => t.globalTrainID === selectedTrain.globalTrainID);
    return hasPosition(hit) ? hit : null;
  }, [renderTrains, selectedTrain]);

  /**
   * Put a train in the middle of the part of the map the user can actually see. The panel
   * covers the right of the map on a desktop and the bottom on a phone, and the plain middle
   * of the map is under it there.
   */
  const centerOnTrain = useCallback((train, { zoom, animate = false } = {}) => {
    const map = mapRef.current;
    if (!map || !hasPosition(train)) return;
    const z = zoom ?? map.getZoom();
    const size = map.getSize();
    const mapBox = map.getContainer().getBoundingClientRect();
    const free = { left: 0, top: 0, right: size.x, bottom: size.y };
    const panelEl = document.querySelector(isMobile ? '.bottom-sheet' : '.train-panel');
    if (panelEl) {
      const box = panelEl.getBoundingClientRect();
      // Only where something is left beside it — a panel over all of the map has no free part
      if (isMobile && box.top - mapBox.top > 80) free.bottom = box.top - mapBox.top;
      if (!isMobile && box.left - mapBox.left > 120) free.right = box.left - mapBox.left;
    }
    const target = L.point((free.left + free.right) / 2, (free.top + free.bottom) / 2);
    const shift = L.point(size.x / 2, size.y / 2).subtract(target);
    const center = map.unproject(map.project([train.lat, train.lng], z).add(shift), z);
    if (animate) map.flyTo(center, z, { duration: 0.8 });
    else map.setView(center, z, { animate: false });
  }, [isMobile]);

  const [followTrain, setFollowTrain] = useState(false);
  // Set while Leaflet flies or zooms; following waits for it to land
  const mapAnimating = useRef(false);

  const locateTrain = () => {
    const map = mapRef.current;
    if (!selectedOnMap || !map) return;
    const zoom = Math.max(map.getZoom(), LOCATE_MIN_ZOOM);
    // Marked here rather than from movestart: the follow listeners are attached a render
    // later, after the flight has begun, and would recentre straight into it
    mapAnimating.current = true;
    map.once('moveend', () => { mapAnimating.current = false; });
    centerOnTrain(selectedOnMap, { zoom, animate: true });
  };

  const toggleFollow = () => {
    if (followTrain) { setFollowTrain(false); return; }
    if (!selectedOnMap) return;
    // Fly there first; the frames after that keep it in place
    locateTrain();
    setFollowTrain(true);
  };

  // A follow belongs to one train: another one, or none, ends it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFollowTrain(false); }, [trainKey(selectedTrain)]);

  // Each repaint recentres the map on the train. Not while Leaflet is flying or zooming — a
  // setView in the middle of that cuts the animation off.
  useEffect(() => {
    const map = mapRef.current;
    if (!followTrain || !map) return undefined;
    const busy = () => { mapAnimating.current = true; };
    const idle = () => { mapAnimating.current = false; };
    // Moving the map by hand means the user wants to look elsewhere — let go of the train
    const letGo = () => setFollowTrain(false);
    // Only zooms mark the map busy: the recentring itself is a move, and listening to
    // movestart would have it block itself
    map.on('zoomstart', busy);
    map.on('zoomend', idle);
    map.on('dragstart', letGo);
    return () => {
      map.off('zoomstart', busy);
      map.off('zoomend', idle);
      map.off('dragstart', letGo);
    };
  }, [followTrain]);

  useEffect(() => {
    if (!followTrain) return undefined;
    // Gone from the snapshot. A train can drop out of one for a moment and be back in the
    // next, so the map holds still for a grace period and only lets go when it stays gone —
    // arrived, cancelled, or out of the data for good.
    if (!selectedOnMap) {
      const timer = setTimeout(() => setFollowTrain(false), FOLLOW_GRACE_MS);
      return () => clearTimeout(timer);
    }
    if (!mapAnimating.current) centerOnTrain(selectedOnMap);
    return undefined;
  }, [followTrain, selectedOnMap, centerOnTrain]);

  const selectedTrainColor = useMemo(() => {
    if (!selectedTrain) return '#58a6ff';
    const type = classifyTrainType(selectedTrain);
    return brandColor(selectedTrain, type) || trainTypeColors[type] || trainTypeColors.default;
  }, [selectedTrain]);

  const selectedIcon = useMemo(() => {
    const train = selectedTrain || markedTrain;
    if (!train) return null;
    const type = classifyTrainType(train);
    const glyph = ICON_MARKUP[type] || ICON_MARKUP.default;
    return L.divIcon({
      className: 'map-selected-icon',
      html: `<span class="map-selected-halo"></span>
             <span class="map-selected-ring"></span>
             <div class="map-selected-wrap" style="transform:rotate(${train.bearing || 0}deg)">
               <span class="map-selected-arrow" style="border-bottom-color:${trainTypeColors[type]}"></span>
             </div>
             <div class="map-selected-glyph">${glyph}</div>`,
      iconSize: [46, 46],
      iconAnchor: [23, 23],
    });
    // Only the identity and heading matter for the markup, not the moving position
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrain, trainKey(markedTrain), markedTrain?.bearing]);

  const handleModeChange = (next) => {
    setMode(next);
    // A train picked at one moment says nothing about another — drop it, keep KBS and
    // station panels, which are per operating day and stay meaningful.
    setPanel(prev => (prev?.type === 'train' ? null : prev));
    if (next === 'kbs') setShowKbs(true);
  };

  // Stable on purpose: react-leaflet unbinds and rebinds a layer's handlers whenever the
  // handler object changes, and the map repaints several times a second
  const handleKbsSelect = useCallback((number) => setPanel({ type: 'kbs', number }), []);
  const handleStationSelect = useCallback((station) => setPanel({ type: 'station', station }), []);
  // Clicking the train on the map is what opens it — including one that a board row only
  // marked. From here on it is a normal selection, so the mark is handed over.
  const handleTrainSelect = useCallback((train) => {
    setHighlightedTrain(null);
    setBoardNotice(null);
    setPanel({ type: 'train', train });
  }, []);

  // One handler object per train for the whole session. It looks the train up when clicked,
  // so it hands over the position of that moment rather than the one it was created with.
  const renderTrainsRef = useRef(renderTrains);
  renderTrainsRef.current = renderTrains;
  const trainHandlers = useRef(new Map());
  const trainHandlersFor = (id) => {
    let handlers = trainHandlers.current.get(id);
    if (!handlers) {
      handlers = {
        click: () => {
          const train = renderTrainsRef.current.find(t => t.globalTrainID === id);
          if (train) handleTrainSelect(train);
        },
      };
      trainHandlers.current.set(id, handlers);
    }
    return handlers;
  };

  // Up to 5000 DOM markers that do not move — built when their inputs change, not per frame
  const stationsShown = showStations && view.zoom >= STATION_MIN_ZOOM;
  const selectedStationUuid = selectedStation?.uuid;
  const stationMarkers = useMemo(() => (
    stationsShown ? stations.map(st => (
      <Marker
        key={st.uuid || st.name}
        position={[st.lat, st.lng]}
        icon={stationIcon(selectedStationUuid === st.uuid)}
        // Above the train clusters: where both overlap the station is what the user
        // switched the layer on for, and a cluster bubble would otherwise eat the click
        zIndexOffset={2000}
        eventHandlers={{ click: () => handleStationSelect(st) }}
      >
        <Tooltip direction="top">{st.name}</Tooltip>
      </Marker>
    )) : null
  ), [stationsShown, stations, selectedStationUuid, handleStationSelect]);
  const closePanel = () => {
    setHighlightedTrain(null);
    setBoardNotice(null);
    setPanel(null);
  };

  // ---- search -------------------------------------------------------------
  const handleSearch = (params) => {
    setPanel({ type: 'search', query: params.query });
    setSearchLoading(true);
    searchTrains(params)
      .then(setSearchResults)
      .catch(() => setSearchResults([]))
      .finally(() => setSearchLoading(false));
  };

  const handleSearchClear = () => {
    setSearchResults([]);
    setPanel(prev => (prev?.type === 'search' ? null : prev));
  };

  // A hit from the search — no station context, so the map aims at the middle of the run
  const handleSearchSelect = (train) => focusTrain({
    trainUUID: train.UUID || train.trainUUID,
    journeyUUID: train.journeyUUID,
    operatingDate: train.operatingDate,
  });

  /**
   * A row from the departure board. The map jumps to the time at exactly this station and
   * marks the train — but the station stays in the panel. Opening the train here would take
   * away the board the user is reading; the train opens when they click it on the map.
   */
  const handleBoardSelect = (journey) => {
    const sr = journey.stationRequest || {};
    setBoardNotice(null);
    focusTrain({
      trainUUID: journey.trainUUID || journey.UUID,
      journeyUUID: journey.journey?.journeyUUID,
      operatingDate: journey.operatingDate,
      atISO: boardType === 'arrival'
        ? (sr.arrivalTime_actual || sr.arrivalTime_scheduled)
        : (sr.departureTime_actual || sr.departureTime_scheduled),
    }, { openPanel: false });
  };

  /**
   * Focus a train that may not be running right now — from the board, the search or from
   * outside the map. Works out when the train is under way and moves the map there.
   */
  const focusTrain = useCallback(async (request, { openPanel = true } = {}) => {
    const { trainUUID, journeyUUID, operatingDate, atISO } = request;
    if (!trainUUID) return;

    const journey = await fetchTrainJourney({
      trainUUID, globalTrainID: trainUUID, journeyUUID, operatingDate,
    }).catch(() => null);

    // Target moment: the time at the station the user came from, otherwise the middle of
    // the run — there the train is under way for sure
    let target = atISO ? new Date(atISO) : null;
    if ((!target || isNaN(target)) && journey?.stations?.length >= 2) {
      const first = journey.stations[0];
      const last = journey.stations[journey.stations.length - 1];
      const start = Date.parse(first.departureTime_actual || first.departureTime_scheduled);
      const end = Date.parse(last.arrivalTime_actual || last.arrivalTime_scheduled);
      if (isFinite(start) && isFinite(end)) target = new Date((start + end) / 2);
    }

    pendingFocus.current = { trainUUID, journey, openPanel };
    // Only take over the panel when it is meant to open. Coming from a station board the
    // station stays on screen — the train is marked on the map and opens on a click there.
    if (openPanel) setSelectedJourney(journey);

    if (target && Math.abs(target.getTime() - Date.now()) > 2 * 60 * 1000) {
      const day = toOperatingDay(target);
      setMode('point');
      setPlaying(false);
      setOpDay(day);
      const [y, m, d] = day.split('-').map(Number);
      const midnight = new Date(y, m - 1, d, 0, 0, 0, 0);
      setTimeMin(Math.round((target.getTime() - midnight.getTime()) / 60000));
    }
    // The snapshot for that moment arrives asynchronously — the effect below picks the
    // train out of it, or falls back to a panel built from the run alone.
  }, []);

  // A selection handed in from outside the map (timetable column, deep link)
  useEffect(() => {
    if (focusRequest?.trainUUID) focusTrain(focusRequest);
  }, [focusRequest, focusTrain]);

  // Resolve a pending focus once the matching snapshot is in
  useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) return;
    const hit = trains.find(t => trainKey(t) === pending.trainUUID);
    if (hit) {
      pendingFocus.current = null;
      if (pending.openPanel) {
        setPanel({ type: 'train', train: hit });
      } else {
        // Mark it and fly there, leave the panel alone
        setHighlightedTrain(trainKey(hit));
        mapRef.current?.flyTo([hit.lat, hit.lng], Math.max(mapRef.current.getZoom(), 10),
          { duration: 0.8 });
      }
      return;
    }
    if (loading) return; // still fetching — wait for the snapshot to settle
    // Not under way at that moment (cancelled, or no run on this day). Show what we know
    // instead of an empty panel.
    pendingFocus.current = null;
    if (pending.journey && pending.openPanel) {
      setPanel({ type: 'train', train: journeyToTrain(pending.journey, pending.trainUUID) });
    } else if (!pending.openPanel) {
      // Came from the board and is not on the map at that moment. Saying so beats leaving
      // the user to wonder why nothing moved.
      setBoardNotice('Dieser Zug ist zu dieser Zeit nicht auf der Karte.');
    }
  }, [trains, loading]);

  const togglePin = (number) => {
    setPinnedKbs(prev => {
      const next = new Set(prev);
      if (next.has(number)) next.delete(number); else next.add(number);
      return next;
    });
  };

  const ampel = mode === 'kbs';

  return (
    <div className="live-map">
      <div className="live-map-canvas">
        <MapContainer
          ref={mapRef}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom
          preferCanvas
          className="live-map-container"
          worldCopyJump={false}
          // Leaflet puts zoom top left by default — straight under the controls panel,
          // where it cannot be clicked. Bottom right is free.
          zoomControl={false}
        >
          <ZoomControl position="bottomright" />
          <ViewTracker onViewChange={setView} />

          {/* The grey map is the colour one with a CSS filter. It used to be CARTO's
              light_all, which now answers every request without an API key with an
              "API KEY REQUIRED" tile. className is only read when the layer is created,
              hence the key: switching remounts the layer. */}
          <TileLayer
            key={mapGrey ? 'grey' : 'color'}
            className={mapGrey ? 'map-tiles-grey' : undefined}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {showOrm && (
            <TileLayer
              url="https://tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png"
              opacity={0.7}
              attribution='&copy; <a href="https://www.openrailwaymap.org/">OpenRailwayMap</a> (CC-BY-SA)'
            />
          )}

          {showKbs && (
            <KbsLayer
              geometry={visibleKbs}
              statusByNumber={statusByNumber}
              ampel={ampel}
              metric={kbsMetric}
              zoom={view.zoom}
              selectedKbs={selectedKbs}
              pinnedKbs={pinnedKbs}
              onSelect={handleKbsSelect}
            />
          )}

          {/* Stations are DOM markers, not canvas dots: a canvas circle is a pixel-hunt to
              hit, and the station panel is one of the main entry points of the page.
              Below STATION_MIN_ZOOM they would overlap into an unclickable carpet. */}
          {stationMarkers}

          {selectedJourney && (
            <TrainRouteLayer journey={selectedJourney} color={selectedTrainColor} />
          )}

          {/* Clustered view when zoomed out — clicking one drills into it */}
          {mode !== 'kbs' && view.zoom < CLUSTER_MAX_ZOOM && clusters.map(c => (
            <Marker
              key={c.key}
              position={[c.lat, c.lng]}
              icon={clusterIcon(c.count, c.delayedShare > 0.3)}
              eventHandlers={{
                click: () => mapRef.current?.setView([c.lat, c.lng], Math.min(view.zoom + 3, 11)),
              }}
            />
          ))}

          {/* Individual trains */}
          {mode !== 'kbs' && view.zoom >= CLUSTER_MAX_ZOOM && drawTrains.map(t => (
            <CircleMarker
              key={t.globalTrainID}
              center={[t.lat, t.lng]}
              radius={t.status === 'cancelled' ? 4 : 5}
              pathOptions={trainDotStyle(t)}
              eventHandlers={trainHandlersFor(t.globalTrainID)}
            />
          ))}

          {/* Labels next to the dot — gated by zoom, the switch and the cap */}
          {mode !== 'kbs' && labelledTrains.filter(hasPosition).map(t => (
            <Marker
              key={`label-${t.globalTrainID}`}
              position={[t.lat, t.lng]}
              icon={trainLabelIcon(t, trainKey(t) === trainKey(emphasisedTrain))}
              eventHandlers={trainHandlersFor(t.globalTrainID)}
            />
          ))}

          {/* The train in focus — halo plus rotated product icon. Selected from the panel, or
              marked because a board row pointed at it; clicking it opens the panel either way */}
          {hasPosition(emphasisedTrain) && selectedIcon && (
            <Marker
              position={[emphasisedTrain.lat, emphasisedTrain.lng]}
              icon={selectedIcon}
              zIndexOffset={1000}
              eventHandlers={{ click: () => handleTrainSelect(emphasisedTrain) }}
            />
          )}
        </MapContainer>

        <MapSearch
          options={options}
          opDay={opDay}
          compact={isMobile}
          onSearch={handleSearch}
          onClear={handleSearchClear}
          busy={searchLoading}
          resultCount={searchResults.length}
        />

        {/* Layer switches */}
        <div className="map-layer-switches">
          <button className={mapGrey ? 'active' : ''} onClick={() => setMapGrey(g => !g)} title="Graustufen-Karte">
            <i className="fa fa-adjust"></i>
          </button>
          <button className={showOrm ? 'active' : ''} onClick={() => setShowOrm(o => !o)} title="OpenRailwayMap-Overlay">
            <i className="fa fa-subway"></i>
          </button>
          <button className={showKbs ? 'active' : ''} onClick={() => setShowKbs(k => !k)} title="Kursbuchstrecken">
            <i className="fa fa-road"></i>
          </button>
          <button
            className={showStations ? 'active' : ''}
            aria-busy={showStations && stationsStatus === 'loading'}
            onClick={() => {
              const next = !showStations;
              setShowStations(next);
              // Turning the layer on while zoomed out would show nothing — take the user there
              if (next && view.zoom < STATION_MIN_ZOOM) {
                mapRef.current?.setZoom(STATION_MIN_ZOOM);
              }
            }}
            title={showStations && stationsStatus === 'loading'
              ? 'Bahnhöfe werden geladen …'
              : 'Bahnhöfe (Klick öffnet Abfahrten und Statistik)'}
          >
            <i className={`fa ${showStations && stationsStatus === 'loading'
              ? 'fa-spinner fa-spin' : 'fa-building-o'}`}></i>
          </button>
          <button
            className={showLabels ? 'active' : ''}
            onClick={() => setShowLabels(s => !s)}
            title={showLabels
              ? 'Beschriftung aus — nur der ausgewählte Zug behält sein Schild'
              : 'Beschriftung für alle Züge einblenden'}
          >
            <i className="fa fa-tags"></i>
          </button>
        </div>

        {/* Traffic-light metric switch — only meaningful in the KBS mode */}
        {ampel && (
          <div className="map-metric-switch">
            <button
              className={kbsMetric === 'punctuality' ? 'active' : ''}
              onClick={() => setKbsMetric('punctuality')}
            >
              Pünktlichkeit
            </button>
            <button
              className={kbsMetric === 'delta' ? 'active' : ''}
              onClick={() => setKbsMetric('delta')}
              title="Ø Verspätungszuwachs je Abschnitt — wo entsteht Verspätung?"
            >
              Δ Zuwachs
            </button>
          </div>
        )}

        {/* Legend */}
        <div className="map-legend">
          {ampel ? (
            kbsMetric === 'delta' ? (
              <>
                <span><i style={{ background: '#3fb950' }}></i> holt auf</span>
                <span><i style={{ background: '#f0b429' }}></i> ±1 min</span>
                <span><i style={{ background: '#f85149' }}></i> &gt; +1 min</span>
                <span><i style={{ background: '#6e7681' }}></i> zu wenig Daten</span>
                <span className="map-legend-title">Ø Zuwachs je Abschnitt</span>
              </>
            ) : (
              <>
                <span><i style={{ background: '#3fb950' }}></i> ≥ 80 %</span>
                <span><i style={{ background: '#f0b429' }}></i> 65–80 %</span>
                <span><i style={{ background: '#f85149' }}></i> &lt; 65 %</span>
                <span><i style={{ background: '#6e7681' }}></i> &lt; 3 Züge</span>
                <span className="map-legend-title">pünktlich (&lt; 6 min)</span>
              </>
            )
          ) : (
            <>
              {DELAY_LEGEND.map(l => (
                <span key={l.label}><i style={{ background: l.color }}></i> {l.label}</span>
              ))}
            </>
          )}
        </div>

        {USE_MOCK && (
          <div className="map-mock-badge" title="Mock-Datenschicht — kein Backend angebunden">
            <i className="fa fa-flask"></i> Mockup
          </div>
        )}

        {/* Notices over the map. The column itself lets every click through to the map,
            so the map stays usable while something is loading; only the notices that offer
            a button take clicks. */}
        <div className="map-notices">
          {boardNotice && (
            <div className="map-board-notice" role="status">
              <i className="fa fa-info-circle"></i> {boardNotice}
              <button onClick={() => setBoardNotice(null)} aria-label="Hinweis schließen">
                <i className="fa fa-times"></i>
              </button>
            </div>
          )}
          {/* The current day is aggregated after the fact — until then every route is grey
              and no route has a drill-in. Saying so beats a map that looks broken. */}
          {mode === 'kbs' && !loading && firstSnapshot && kbsStatus.length === 0 && (
            <div className="map-board-notice" role="status">
              <i className="fa fa-info-circle"></i> Für {opDay} liegt noch keine Streckenauswertung vor.
              <button className="map-load-retry" onClick={() => setOpDay(d => shiftOperatingDay(d, -1))}>
                Vortag anzeigen
              </button>
            </div>
          )}
          {showStations && stationsStatus === 'loading' && (
            <div className="map-load-pill" role="status">
              <i className="fa fa-spinner fa-spin"></i> Bahnhöfe werden geladen …
            </div>
          )}
          {showStations && stationsStatus === 'failed' && (
            <div className="map-board-notice map-load-failed" role="alert">
              <i className="fa fa-exclamation-triangle"></i> Bahnhöfe konnten nicht geladen werden.
              <button className="map-load-retry" onClick={retryStations}>Erneut versuchen</button>
            </div>
          )}
        </div>

        {/* On a phone the controls are not an overlay at all: two floating buttons sit in
            thumb reach and open the controls as a sheet. A panel anchored to the bottom of
            the map fell below the fold whenever the viewport was short, and the map swallows
            the drag that would have scrolled to it. */}
        {isMobile && !panel && (
          <div className="map-fabs">
            <button
              className={`map-fab${controlsSheetOpen ? ' active' : ''}`}
              onClick={() => setControlsSheetOpen(o => !o)}
              aria-label="Steuerung und Filter"
              title="Steuerung und Filter"
            >
              <i className="fa fa-sliders"></i>
              {activeFilters > 0 && <span className="map-fab-badge">{activeFilters}</span>}
            </button>
            <button
              className="map-fab"
              onClick={scrollToDashboard}
              aria-label="Zur Tagesauswertung"
              title="Zur Tagesauswertung"
            >
              <i className="fa fa-bar-chart"></i>
            </button>
          </div>
        )}

        <PanelHost
          // Desktop: always mounted, it collapses itself. Phone: only while its sheet is open
          open={!isMobile || (controlsSheetOpen && !panel)}
          mobile={isMobile}
          onClose={() => setControlsSheetOpen(false)}
        >
        <MapControls
          embedded={isMobile}
          collapsed={!isMobile && controlsCollapsed}
          onToggleCollapsed={
            isMobile ? () => setControlsSheetOpen(false) : () => setControlsCollapsed(c => !c)
          }
          mode={mode}
          onModeChange={handleModeChange}
          opDay={opDay}
          onOpDayChange={setOpDay}
          timeMin={timeMin}
          onTimeMinChange={setTimeMin}
          playing={playing}
          onPlayingChange={setPlaying}
          speed={speed}
          onSpeedChange={setSpeed}
          filters={filters}
          onFiltersChange={setFilters}
          options={options}
          stats={stats}
          loading={loading}
        />
        </PanelHost>

        <PanelHost open={!!panel} mobile={isMobile} onClose={closePanel}>
        {panel?.type === 'kbs' && (
          <KbsDrillIn
            detail={kbsDetail}
            route={selectedKbsInfo || { kbs_number: selectedKbs }}
            opDay={opDay}
            loading={kbsDetailLoading}
            failed={kbsDetailFailed}
            onShowPreviousDay={() => setOpDay(d => shiftOperatingDay(d, -1))}
            pinned={pinnedKbs.has(selectedKbs)}
            onTogglePin={() => togglePin(selectedKbs)}
            onClose={closePanel}
          />
        )}

        {panel?.type === 'station' && (
          <StationPanel
            station={selectedStation}
            stats={stationStats}
            loading={stationLoading}
            board={board}
            boardLoading={boardLoading}
            boardType={boardType}
            onBoardTypeChange={setBoardType}
            onJourneySelect={handleBoardSelect}
            onClose={closePanel}
          />
        )}

        {panel?.type === 'search' && (
          <SearchResultsPanel
            results={searchResults}
            loading={searchLoading}
            query={panel.query}
            onSelect={handleSearchSelect}
            onClose={closePanel}
          />
        )}

        {panel?.type === 'train' && liveSelectedTrain && (
          <TrainPanel
            key={trainKey(liveSelectedTrain)}
            train={liveSelectedTrain}
            journey={selectedJourney}
            journeyLoading={journeyState === 'loading'}
            journeyFailed={journeyState === 'failed'}
            opDay={mode === 'live' ? null : opDay}
            at={atDate}
            showFullDetailLink={FEATURES.trainDetailSection}
            onOpenDetail={onTrainSelect}
            onSelectKbs={handleKbsSelect}
            onClose={closePanel}
            onMap={!!selectedOnMap}
            onLocate={locateTrain}
            following={followTrain}
            onToggleFollow={toggleFollow}
          />
        )}
        </PanelHost>
      </div>

      {/* Kennzahlen-Leiste */}
      <div className="map-kpi-bar">
        {mode === 'kbs' ? (
          <>
            <span className="map-kpi"><b>{stats.kbsCount}</b> Strecken</span>
            <span className="map-kpi"><b>{stats.kbsTrains}</b> Zug-Zuordnungen</span>
            <span className="map-kpi-hint">Betriebstag {opDay} · Klick auf eine Strecke öffnet die Auswertung</span>
          </>
        ) : (
          <>
            <span className="map-kpi">
              on-time gesamt <b>{stats.onTimePct !== null ? `${stats.onTimePct.toFixed(1)} %` : '–'}</b>
            </span>
            {['fern', 'nah', 'sbahn'].map(group => (
              <span className="map-kpi" key={group}>
                {trainGroupLabels[group]}{' '}
                <b>{stats.byGroup[group] !== null ? `${stats.byGroup[group].toFixed(0)} %` : '–'}</b>
              </span>
            ))}
            <span className="map-kpi">Ausfälle <b>{stats.cancelled}</b></span>
            <span className="map-kpi">Ø Verspätung <b>+{stats.avgDelay.toFixed(1)} min</b></span>
            <span className="map-kpi-hint">
              {mode === 'live' ? 'Live · Aktualisierung alle 60 s' : `Zeitpunkt ${opDay}`}
            </span>
          </>
        )}
      </div>

      <div ref={dashboardRef}>
        <NetworkDashboard
          stats={networkStats}
          loading={networkLoading}
          opDay={opDay}
          open={dashboardOpen}
          onToggle={() => setDashboardOpen(o => !o)}
        />
      </div>
    </div>
  );
};

export default LiveMap;
